// Article #7 — How Much Does Pool Resurfacing Cost in South Florida?
//
// This is the shared data file for the article. The TOC array below controls
// the visible linked table of contents. The FAQ array controls both the
// visible FAQ section and the FAQPage JSON-LD schema. During the preparation
// phase both arrays are complete-but-the-FAQ-array-empty; Prompt #2 will
// populate the finished article copy and the final eight FAQs.

export interface TOCItem {
  id: string;
  label: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export const TOC_POOL_RESURFACING_COST: TOCItem[] = [
  { id: "the-short-answer", label: "The Short Answer" },
  {
    id: "why-pool-resurfacing-prices-vary",
    label: "Why Pool Resurfacing Prices Vary So Much",
  },
  {
    id: "typical-pool-resurfacing-cost-ranges",
    label: "Typical Pool Resurfacing Cost Ranges in South Florida",
  },
  {
    id: "how-finish-selection-changes-price",
    label: "How Finish Selection Changes the Price",
  },
  {
    id: "pool-size-shape-and-surface-area",
    label: "Pool Size, Shape, and Interior Surface Area",
  },
  {
    id: "surface-preparation-and-existing-condition",
    label: "Surface Preparation and Existing Pool Condition",
  },
  {
    id: "what-a-pool-resurfacing-quote-may-include",
    label: "What a Pool Resurfacing Quote May Include",
  },
  {
    id: "costs-outside-the-resurfacing-quote",
    label: "Costs That May Be Outside the Resurfacing Quote",
  },
  {
    id: "when-other-components-change-project-scope",
    label: "When Tile, Coping, or Decking Changes the Project Scope",
  },
  {
    id: "how-to-compare-pool-resurfacing-estimates",
    label: "How to Compare Pool Resurfacing Estimates",
  },
  {
    id: "questions-to-ask-before-accepting-a-quote",
    label: "Questions to Ask Before Accepting a Quote",
  },
  {
    id: "budgeting-for-the-right-scope",
    label: "Budgeting for the Right Scope",
  },
  {
    id: "frequently-asked-questions",
    label: "Frequently Asked Questions",
  },
];

// Eight finished FAQs. Both the visible FAQ section (BodyPart3.tsx) and the
// FAQPage JSON-LD schema (the route's head()) read from this same array, so
// the visible answers and structured data always match exactly.
export const FAQS_POOL_RESURFACING_COST: FAQItem[] = [
  {
    question: "How much does pool resurfacing cost in South Florida?",
    answer:
      "Current published South Florida market guides commonly place residential pool resurfacing between approximately $4,500 and $18,000 or more. Traditional plaster generally occupies the lower portion of that range, quartz finishes tend to fall in the middle, and premium pebble or exposed-aggregate finishes usually cost more. Pool size, existing condition, preparation, access, and included services can move the final price outside these ranges.",
  },
  {
    question: "What is the cheapest type of pool finish?",
    answer:
      "Traditional white plaster or marcite is generally the lowest-priced major finish category. However, the lowest initial price does not establish the best overall choice. Homeowners should compare the exact product, appearance, texture, maintenance expectations, preparation, and written scope.",
  },
  {
    question: "Why are two pool-resurfacing estimates so different?",
    answer:
      "The estimates may assume different finishes, surface areas, preparation methods, repair allowances, fittings, startup services, refill responsibilities, or exclusions. Compare the written scopes line by line before deciding that the prices represent the same project.",
  },
  {
    question: "Does pool-resurfacing pricing include waterline tile?",
    answer:
      "Not necessarily. Waterline tile is a separate component and should be identified clearly as included, repaired, replaced, protected, or excluded. Never assume tile work is included solely because the pool is being resurfaced.",
  },
  {
    question: "Are coping and pool-deck work included with resurfacing?",
    answer:
      "Usually, coping and decking should be treated as separate scope items unless the proposal expressly includes them. Adding either can turn a resurfacing-only project into a broader pool-remodeling project.",
  },
  {
    question: "Does the resurfacing price include refilling and startup?",
    answer:
      "It depends on the proposal. Some estimates include certain refill or startup services, while others exclude the water, chemicals, brushing, testing, or follow-up visits. Confirm each responsibility in writing.",
  },
  {
    question:
      "Can a contractor give an accurate pool-resurfacing price from photographs?",
    answer:
      "Photographs can help begin the conversation, but they cannot reliably reveal every condition, measurement, layer, hollow area, access issue, or repair need. A dependable proposal generally requires more information and an appropriate assessment of the actual pool.",
  },
  {
    question: "Is the most expensive pool finish always the best?",
    answer:
      "No. A higher-priced finish may offer a different aggregate, texture, appearance, or product system, but it is not automatically the best fit for every homeowner. The right choice depends on the pool, budget, design goals, texture preference, maintenance expectations, and properly defined installation scope.",
  },
];
